<template>
  <div :class="['form-element', hidden && 'd-none']">
    <FormLabel
      v-if="label"
      v-bind="{
        label,
        required,
        disabled,
        description,
        descriptionType,
        dirty,
        error,
        id
      }"
    />
    <div>
      <section v-if="normalizedValue.length">
        <AppSortable
          :value="normalizedValue"
          class="border rounded-5 mt-2 mb-4"
          :keySuffix="id"
          @input="update"
        >
          <template #default="{ item: courseGroup, key }">
            <div
              class="d-flex border-bottom pr-2 py-2 pl-3 align-items-center"
              title="Drag to sort"
            >
              <AppIcon
                name="drag_indicator"
                class="mr-2"
                style="opacity:0.5"
              />
              <div
                class="mr-2 flex-1"
              >
                <div v-for="(value, index) in courseGroup.value.filter(g => g)" :key="index" class="d-flex mt-2">
                  <AppTooltip class="flex-0 d-inline-block">
                    <AppButton
                      :disabled="courseGroup.value.filter(g => g).length <= 1"
                      variant="danger"
                      style="height:24px;"
                      class="px-2 rounded-5"
                      @click="deleteValueForExistingCourseGroup(value, key)"
                    >
                      <AppIcon name="remove_circle_outline" />
                    </AppButton>
                    <template #tooltip>Remove</template>
                  </AppTooltip>
                  <span class="flex-1">
                    <strong v-if="index > 0">{{ courseGroup.logic.toUpperCase() }} </strong>
                    <span v-html="getCourseLabel(value)" />
                  </span>
                  <br />
                </div>
                <div class="d-flex mt-2">
                  <AppTooltip v-if="emptyCourseExists(courseGroup)" class="flex-0">
                    <AppButton
                      variant="danger"
                      style="height:24px;"
                      class="px-2 rounded-5"
                      @click="deleteValueForExistingCourseGroup('', key)"
                    >
                      <AppIcon name="remove_circle_outline" />
                    </AppButton>
                    <template #tooltip>Remove</template>
                  </AppTooltip>
                  <AppTooltip class="flex-0">
                    <AppButton
                      v-if="emptyCourseExists(courseGroup)"
                      class="mr-2 rounded-5 bg-light px-2 d-flex align-items-center"
                      style="height:30px;"
                      data-test="toggle_logic"
                      @click="courseGroup.logic = getUnselectedLogicForExistingCourseGroup(courseGroup.logic)"
                    >
                      {{ courseGroup.logic }}
                    </AppButton>
                    <template #tooltip>Change to <strong>
                      {{ getUnselectedLogicForExistingCourseGroup(courseGroup.logic).toUpperCase() }}
                    </strong></template>
                  </AppTooltip>
                  <div class="flex-1">
                    <div v-for="(value, index) in courseGroup.value.filter(g => !g)" :key="index">
                      <FormCourseSelect
                        :label="`Select ${$localize('course')}`"
                        :value="value"
                        :excludedIds="[...excludeIds,...courseGroup.value]"
                        :config="{...config, multiple: false, includePending: true }"
                        placeholder="Type here to start searching"
                        class="flex-1"
                        :useFetchingRequisitesLatestRevisionSettings="useFetchingRequisitesLatestRevisionSettings"
                        :addCourseGroupById="v => addNewValueToExistingCourseGroup(v, key)"
                        :useCustomCourseFilter="true"
                        :index="key + 2"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <AppTooltip>
                <AppButton
                  :raised="true"
                  :disabled="emptyCourseExists(courseGroup)"
                  variant="muted"
                  class="mr-2 rounded-5 px-2 d-flex align-items-center"
                  style="height:30px;"
                  data-test="add_requisite_course"
                  @click="() => addNewCourseToCourseGroup(courseGroup)"
                >
                  <AppIcon name="add" />
                </AppButton>
                <template #tooltip>Add additional courses</template>
              </AppTooltip>
              <AppTooltip>
                <AppButton
                  variant="danger"
                  class="rounded-5 px-2 d-flex align-items-center"
                  @click="deleteCourseGroup(key)"
                >
                  <AppIcon name="clear" />
                </AppButton>
                <template #tooltip>Delete</template>
              </AppTooltip>
            </div>
          </template>
        </AppSortable>
      </section>
      <div
        v-for="(item,i) in courseGroup.value"
        :key="i"
        class="d-flex align-items-start"
      >
        <FormCourseSelect
          v-model="courseGroup.value[i]"
          :label="`Select ${$localize('course')}`"
          :excludedIds="[...excludeIds,...courseGroup.value]"
          :config="{...config, multiple: false, includePending: true }"
          placeholder="Type here to start searching"
          class="flex-1"
          :useFetchingRequisitesLatestRevisionSettings="useFetchingRequisitesLatestRevisionSettings"
          :addCourseGroupById="addCourseGroupById"
          :useCustomCourseFilter="true"
          :index="i + 1"
        />
      </div>
    </div>
  </div>
</template>
<script>
import FormMixin from './FormMixin'
import FormLabel from './FormLabel'
import FormCourseSelect from './FormCourseSelect'
// eslint-disable-next-line no-restricted-imports
import useCourse from '@cm/client/composables/useCourse'
import { inject } from '@vue/composition-api'
import _isObject from 'lodash/isObject'
import useSharedCourses from '@/components/composables/useSharedCourses'
import useSchoolSettings from '@/components/composables/useSchoolSettings'

export default {
  mixins: [ FormMixin ],
  components: { FormLabel, FormCourseSelect },
  props: {
    excludeIds: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Array,
      default: () => [],
    },
    useFetchingRequisitesLatestRevisionSettings: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      courseGroup: {
        value: [ undefined ], // setting undefined so that the field shows up
        logic: 'and',
      },
    }
  },
  setup () {
    const effectiveDatesRange = inject('effectiveDatesRange', '')
    const {
      generateCourseLabel,
    } = useCourse()
    const { getCourseByDatesRangeLookup } = useSharedCourses()
    const { schoolSettings } = useSchoolSettings()

    return {
      generateCourseLabel,
      getCourseByDatesRangeLookup,
      schoolSettings,
      effectiveDatesRange,
    }
  },
  computed: {
    normalizedValue () { // Only used untill all data is updated to support and/or logic
      return this.value.map(v => _isObject(v) ? v : { value: [ v ], logic: 'and' })
    },
    currentSelections () {
      return this.courseGroup.value.filter(Boolean)
    },
    unselectedLogic () {
      return [ 'and', 'or' ].find(logic => logic !== this.courseGroup.logic)
    },
  },
  methods: {
    addNewValueToExistingCourseGroup (value, key) {
      this.normalizedValue[key].value[this.normalizedValue[key].value.length - 1] = value
      this.$emit('input', this.normalizedValue)
    },
    deleteValueForExistingCourseGroup (value, key) {
      const currentValue = this.normalizedValue[key].value
      const index = currentValue.findIndex(v => v === value)
      if (index > -1) {
        this.normalizedValue[key].value.splice(index, 1)
        this.$emit('input', this.normalizedValue)
      }
    },
    deleteCourseGroup (key) {
      this.$emit('input', this.normalizedValue.filter((courseGroup, index) => index !== key))
    },
    addCourseGroupById (id) {
      if (id) {
        this.$emit('input', [ ...this.normalizedValue, { ...this.courseGroup, value: [ id ] } ])
        this.courseGroup.value = [ undefined ]
      }
    },
    getCourseLabel (courseId) {
      const useFetchRequisitesWithLatestRevision = this.schoolSettings?.enableFetchingRequisitesWithLatestRevisions
      // the following setting, if enabled, will display the ID of the course in the UI if the
      // ID cannot be matched to a course. If disabled, we will display "course not found"
      const displayRequisiteIdInlineIfEntityNotFound = this.schoolSettings?.displayRequisiteIdInlineIfEntityNotFound
      const course = this.getCourseByDatesRangeLookup({
        courseGroupId: { value: courseId },
        effectiveDatesRange: { value: this.effectiveDatesRange },
        useFetchRequisitesWithLatestRevision,
      })
      return `<span>${this.generateCourseLabel(course, displayRequisiteIdInlineIfEntityNotFound ? courseId : null)}</span>`
    },
    addNewCourseToCourseGroup (courseGroup) {
      courseGroup.value.push('')
    },
    getUnselectedLogicForExistingCourseGroup (cgLogic) {
      return [ 'and', 'or' ].find(logic => logic !== cgLogic)
    },
    emptyCourseExists (courseGroup) {
      return courseGroup.value.filter(g => !g).length > 0
    },
  },

}
</script>
